import { DropGranularity } from '@/ApiClient';
import { getISOWeek, getQuarter, format } from 'date-fns';

/**
 * Formats a drop date based on its granularity.
 * - Week: w7-25 (week 7 of 2025)
 * - Quarter: q1-25 (quarter 1 of 2025)
 * - Month: jan-25 (January 2025)
 * - Day: 2025-01-15 (ISO date format)
 */
export function formatDrop(dropDate: string, granularity: DropGranularity): string {
  const date = new Date(dropDate);
  const yearShort = date.getFullYear() % 100;

  switch (granularity) {
    case DropGranularity.Week: {
      const week = getISOWeek(date);
      return `w${week}-${yearShort}`;
    }

    case DropGranularity.Quarter: {
      const quarter = getQuarter(date);
      return `q${quarter}-${yearShort}`;
    }

    case DropGranularity.Month: {
      const monthName = format(date, 'MMM').toLowerCase();
      return `${monthName}-${yearShort}`;
    }

    case DropGranularity.Day:
    default: {
      return format(date, 'yyyy-MM-dd');
    }
  }
}

/**
 * Formats a drop date without the year for display in dropdowns and labels.
 * - Week: w7
 * - Quarter: Q1
 * - Month: Jan
 * - Day: 1st Jan (verbose) or 1/1 (compact)
 */
export function formatDropWithoutYear(
  dropDate: string,
  granularity: DropGranularity,
  dayFormat: 'verbose' | 'compact' = 'verbose'
): string {
  const date = new Date(dropDate);

  switch (granularity) {
    case DropGranularity.Week: {
      const week = getISOWeek(date);
      return `w${week}`;
    }

    case DropGranularity.Quarter: {
      const quarter = getQuarter(date);
      return `Q${quarter}`;
    }

    case DropGranularity.Month: {
      return format(date, 'MMM');
    }

    case DropGranularity.Day:
    default: {
      if (dayFormat === 'compact') {
        return format(date, 'MMM d');
      }
      return `${format(date, 'do')} ${format(date, 'MMM')}`;
    }
  }
}

/**
 * Gets the date range for a drop based on its granularity.
 * Returns { start: Date, end: Date } representing the period covered by this drop.
 */
export function getDropDateRange(dropDate: string, granularity: DropGranularity): { start: Date; end: Date } {
  const date = new Date(dropDate);

  switch (granularity) {
    case DropGranularity.Week: {
      // Get first day of week (Monday)
      const dayOfWeek = (date.getDay() + 6) % 7; // Make Monday = 0
      const firstDay = new Date(date);
      firstDay.setDate(date.getDate() - dayOfWeek);

      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);

      return { start: firstDay, end: lastDay };
    }

    case DropGranularity.Quarter: {
      const quarter = Math.floor(date.getMonth() / 3);
      const firstMonth = quarter * 3;

      const start = new Date(date.getFullYear(), firstMonth, 1);
      const end = new Date(date.getFullYear(), firstMonth + 3, 0); // Last day of last month in quarter

      return { start, end };
    }

    case DropGranularity.Month: {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of month

      return { start, end };
    }

    case DropGranularity.Day:
    default: {
      // For day granularity, start and end are the same
      return { start: date, end: date };
    }
  }
}
